import { Trade, TradeType, TokenAmount, CurrencyAmount } from '@uniswap/sdk'
import React, { useContext, useMemo } from 'react'
import { ArrowDown, AlertTriangle } from 'react-feather'
import { Text } from 'rebass'
import { ThemeContext } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Field } from '../../state/swap/actions'
import { TYPE } from '../../theme'
import { ButtonPrimary } from '../Button'
import { isAddress, shortenAddress } from '../../utils'
import { computeSlippageAdjustedAmounts, computeTradePriceBreakdown, warningSeverity } from '../../utils/prices'
import { getTaxAdjustedOutputAmount, getTaxAdjustedAmounts } from '../../hooks/Trades'
import { AutoColumn } from '../Column'
import CurrencyLogo from '../CurrencyLogo'
import { RowBetween, RowFixed } from '../Row'
import { TruncatedText, SwapShowAcceptChanges } from './styleds'

export default function SwapModalHeader({
  trade,
  allowedSlippage,
  recipient,
  showAcceptChanges,
  onAcceptChanges
}: {
  trade: Trade
  allowedSlippage: number
  recipient: string | null
  showAcceptChanges: boolean
  onAcceptChanges: () => void
}) {
  // Get tax-adjusted amounts first
  const taxAdjustedAmounts = useMemo(() => getTaxAdjustedAmounts(trade), [trade])
  
  // Calculate slippage on tax-adjusted amounts
  const slippageAdjustedAmounts = useMemo(() => {
    // If we have tax adjustments, apply slippage to the adjusted amounts
    if (taxAdjustedAmounts.adjustedOutputAmount && taxAdjustedAmounts.adjustedOutputAmount !== trade.outputAmount) {
      const originalSlippageAmounts = computeSlippageAdjustedAmounts(trade, allowedSlippage)
      
      // Calculate tax ratio and apply to slippage amounts
      const taxRatio = parseFloat(taxAdjustedAmounts.adjustedOutputAmount.divide(trade.outputAmount).toSignificant(10))
      
      const originalOutput = originalSlippageAmounts[Field.OUTPUT]
      if (originalOutput) {
        const adjustedRaw = Math.floor(parseFloat(originalOutput.raw.toString()) * taxRatio)
        const adjustedSlippageOutput = originalOutput instanceof TokenAmount
          ? new TokenAmount(originalOutput.token, adjustedRaw.toString())
          : CurrencyAmount.ether(adjustedRaw.toString())
        
        return {
          ...originalSlippageAmounts,
          [Field.OUTPUT]: adjustedSlippageOutput
        }
      }
    }
    
    return computeSlippageAdjustedAmounts(trade, allowedSlippage)
  }, [trade, allowedSlippage, taxAdjustedAmounts])
  
  const { priceImpactWithoutFee } = useMemo(() => computeTradePriceBreakdown(trade), [trade])
  const priceImpactSeverity = warningSeverity(priceImpactWithoutFee)
  
  // Calculate tax-adjusted output amount
  const taxAdjustedOutputAmount = useMemo(() => getTaxAdjustedOutputAmount(trade), [trade])

  const theme = useContext(ThemeContext)
  const { t } = useTranslation()

  return (
    <AutoColumn gap={'md'} style={{ marginTop: '20px' }}>
      <RowBetween align="flex-end">
        <RowFixed gap={'0px'}>
          <CurrencyLogo currency={trade.inputAmount.currency} size={'24px'} style={{ marginRight: '12px' }} />
          <TruncatedText
            fontSize={24}
            fontWeight={500}
            color={showAcceptChanges && trade.tradeType === TradeType.EXACT_OUTPUT ? theme.primary1 : ''}
          >
            {trade.inputAmount.toSignificant(6)}
          </TruncatedText>
        </RowFixed>
        <RowFixed gap={'0px'}>
          <Text fontSize={24} fontWeight={500} style={{ marginLeft: '10px' }}>
            {trade.inputAmount.currency.symbol}
          </Text>
        </RowFixed>
      </RowBetween>
      <RowFixed>
        <ArrowDown size="16" color={theme.text2} style={{ marginLeft: '4px', minWidth: '16px' }} />
      </RowFixed>
      <RowBetween align="flex-end">
        <RowFixed gap={'0px'}>
          <CurrencyLogo currency={trade.outputAmount.currency} size={'24px'} style={{ marginRight: '12px' }} />
          <TruncatedText
            fontSize={24}
            fontWeight={500}
            color={
              priceImpactSeverity > 2
                ? theme.red1
                : showAcceptChanges && trade.tradeType === TradeType.EXACT_INPUT
                ? theme.primary1
                : ''
            }
          >
            {(taxAdjustedOutputAmount || trade.outputAmount).toSignificant(6)}
          </TruncatedText>
        </RowFixed>
        <RowFixed gap={'0px'}>
          <Text fontSize={24} fontWeight={500} style={{ marginLeft: '10px' }}>
            {trade.outputAmount.currency.symbol}
          </Text>
        </RowFixed>
      </RowBetween>
      {showAcceptChanges ? (
        <SwapShowAcceptChanges justify="flex-start" gap={'0px'}>
          <RowBetween>
            <RowFixed>
              <AlertTriangle size={20} style={{ marginRight: '8px', minWidth: 24 }} />
              <TYPE.main color={theme.primary1}>{t('priceUpdated')}</TYPE.main>
            </RowFixed>
            <ButtonPrimary
              style={{ padding: '.5rem', width: 'fit-content', fontSize: '0.825rem', borderRadius: '12px' }}
              onClick={onAcceptChanges}
            >
              {t('accept')}
            </ButtonPrimary>
          </RowBetween>
        </SwapShowAcceptChanges>
      ) : null}
      <AutoColumn justify="flex-start" gap="sm" style={{ padding: '12px 0 0 0px' }}>
        {trade.tradeType === TradeType.EXACT_INPUT ? (
          <TYPE.italic textAlign="left" style={{ width: '100%' }}>
            {t('outputEstimated')}{' '}
            <b>
              {slippageAdjustedAmounts[Field.OUTPUT]?.toSignificant(6)} {trade.outputAmount.currency.symbol}
            </b>
            {t('orTxWillRevert')}
          </TYPE.italic>
        ) : (
          <TYPE.italic textAlign="left" style={{ width: '100%' }}>
            {t('inputEstimated')}{' '}
            <b>
              {slippageAdjustedAmounts[Field.INPUT]?.toSignificant(6)} {trade.inputAmount.currency.symbol}
            </b>
            {t('orTxWillRevert')}
          </TYPE.italic>
        )}
      </AutoColumn>
      {recipient !== null ? (
        <AutoColumn justify="flex-start" gap="sm" style={{ padding: '12px 0 0 0px' }}>
          <TYPE.main>
            {t('outputWillBeSentTo')} <b title={recipient}>{isAddress(recipient) ? shortenAddress(recipient) : recipient}</b>
          </TYPE.main>
        </AutoColumn>
      ) : null}
    </AutoColumn>
  )
}
